import {
  createContext,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Autocomplete,
  AutocompleteProps,
  Option,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { messagesActions } from 'src/store/messages/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { defaultServerError } from 'src/utils/consts';
import { debounce } from 'src/utils/debounce';
import { noop } from 'src/utils/noop';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

import classes from './AsyncAutocomplete.module.scss';

interface AsyncAutocompleteContextValue {
  loadMore: (searchText: string, clear?: boolean) => Promise<void>;
  hasMore: RefObject<boolean>;
  last: RefObject<string | number | undefined>;
}

const AsyncAutocompleteContext = createContext<
  AsyncAutocompleteContextValue | undefined
>(undefined);

interface AsyncOptionProps<T> {
  searchText: string;
  option: Option<T>;
  renderValue: (option: Option<T>) => string;
  makeOption?: (option: Option<T>, inputValue: string) => JSX.Element | null;
  showTooltip?: boolean;
}

const AsyncOption = <T,>({
  searchText,
  option,
  renderValue,
  makeOption,
  showTooltip,
}: AsyncOptionProps<T>): ReactElement => {
  const { loadMore, last, hasMore } = useRequiredContext(
    AsyncAutocompleteContext,
  );
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore.current) return;

    if (!last.current) return;

    if (!observedRef.current) return;

    if (option.id !== last.current) return;

    observerRef.current = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadMore(searchText);
          observer.unobserve(entry.target);
        }
      });
    });

    observerRef.current.observe(observedRef.current);
  }, [hasMore, last, loadMore, option.id, searchText]);

  const optionNode = useMemo(() => {
    if (makeOption) return makeOption(option, searchText);

    const value = renderValue(option);

    return (
      <TooltipWrapper title={value} disableHover={!showTooltip}>
        <Typography variant='body2' ellipsis={true}>
          {renderValue(option)}
        </Typography>
      </TooltipWrapper>
    );
  }, [makeOption, option, renderValue, searchText, showTooltip]);

  return (
    <div ref={observedRef} className={classes.optionContainer}>
      {optionNode}
    </div>
  );
};

export interface AsyncAutocompleteProps<T>
  extends Omit<
    AutocompleteProps<T>,
    'options' | 'loading' | 'onInputClear' | 'onInputChange'
  > {
  onInputClear?: () => void;
  onInputChange?: (input: string) => void;
  delayMs?: number;
  itemLimit?: number;
  minSearchLength?: number;
  minSearch?: boolean;
  fetchOptions: (
    search: string,
    limit: number,
    offset: number,
  ) => Promise<Option<T>[]>;
}

export const AsyncAutocomplete = <T,>({
  value,
  delayMs = 500,
  itemLimit = 20,
  minSearchLength = 3,
  minSearch,
  noOptionsText: noOptionsTextFromProps,
  fetchOptions,
  renderValue,
  onInputClear = noop,
  onInputChange,
  onOpen,
  makeOption,
  showTooltip,
  ...rest
}: AsyncAutocompleteProps<T>): ReactElement => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const itemOffset = useRef(0);
  const [options, setOptions] = useState<Option<T>[]>([]);
  const hasMore = useRef(true);
  const requestUUID = useRef<string | undefined>();
  const last = useRef<string | number | undefined>();
  const [lessThanMinSearch, setLessThanMinSearch] = useState(true);
  const initialized = useRef(false);

  const noOptionsText =
    minSearch && lessThanMinSearch
      ? `Введите не менее ${minSearchLength} символов`
      : noOptionsTextFromProps;

  const getOptions = useCallback(
    async (searchText: string, clear?: boolean) => {
      const currentRequestUUID = crypto.randomUUID();

      try {
        if (minSearch && searchText.length < minSearchLength) {
          setOptions([]);
          return;
        }

        if (clear) {
          hasMore.current = true;
          itemOffset.current = 0;
        }

        if (!hasMore.current) return;

        requestUUID.current = currentRequestUUID;

        const newOptions = await fetchOptions(
          searchText,
          itemLimit,
          itemOffset.current,
        );

        if (currentRequestUUID !== requestUUID.current) return;

        // eslint-disable-next-line require-atomic-updates
        if (newOptions.length < itemLimit) hasMore.current = false;
        last.current = newOptions[newOptions.length - 1]?.id;
        itemOffset.current += newOptions.length;

        setOptions(prev => {
          if (clear) return newOptions;

          return [...prev, ...newOptions];
        });

        initialized.current = true;
      } catch (e) {
        dispatch(messagesActions.show({ message: defaultServerError }));
      } finally {
        if (currentRequestUUID !== requestUUID.current) setLoading(false);
      }
    },
    [dispatch, fetchOptions, itemLimit, minSearch, minSearchLength],
  );

  const debouncedGetOptions = useMemo(
    () => debounce(getOptions, delayMs),
    [delayMs, getOptions],
  );

  const handleInputChange = useCallback(
    (input: string) => {
      setLessThanMinSearch(input.length < minSearchLength);
      debouncedGetOptions(input, true);
      onInputChange?.(input);
    },
    [debouncedGetOptions, minSearchLength, onInputChange],
  );

  const resultMakeOption = useCallback(
    (option: Option<T>, inputValue: string): JSX.Element => (
      <AsyncOption
        option={option}
        searchText={inputValue}
        renderValue={renderValue}
        makeOption={makeOption}
        showTooltip={showTooltip}
      />
    ),
    [makeOption, renderValue, showTooltip],
  );

  const contextValue = useMemo(
    (): AsyncAutocompleteContextValue => ({
      loadMore: getOptions,
      last,
      hasMore,
    }),
    [getOptions],
  );

  const handleOpen = useCallback(() => {
    if (!initialized.current) getOptions(value ? renderValue(value) : '', true);
    onOpen?.();
  }, [getOptions, onOpen, renderValue, value]);

  useEffect(() => {
    setOptions([]);
    hasMore.current = true;
    itemOffset.current = 0;
    initialized.current = false;
  }, [getOptions]);

  return (
    <AsyncAutocompleteContext.Provider value={contextValue}>
      <Autocomplete<T>
        {...rest}
        value={value}
        onInputClear={onInputClear}
        renderValue={renderValue}
        options={options}
        noOptionsText={noOptionsText}
        makeOption={resultMakeOption}
        onInputChange={handleInputChange}
        loading={loading && !options.length}
        onOpen={handleOpen}
      />
    </AsyncAutocompleteContext.Provider>
  );
};
